import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Upload, Music, Trash2, Loader2, LogOut, Save, Play, Pause } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const resolveSrc = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : '');
const formatDuration = (ms) => {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Producer's private upload dashboard.
 *
 * Bulk-first flow: producer picks ONE energy + genre set that applies to
 * the whole drop (a producer uploading a "deephouse-groovy" batch tags
 * once, uploads many). Per-file we only ask for title (auto-filled from
 * the filename) and artist. Cuts the per-track UX from ~10 taps down
 * to zero.
 */
export default function ProducerDashboardPage() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ energies: [], genres: [] });
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Batch-level tags apply to every file staged this session
  const [batchEnergy, setBatchEnergy] = useState('');
  const [batchGenres, setBatchGenres] = useState([]);
  const [staged, setStaged] = useState([]);   // {file, title, artist, status, error}
  const [uploading, setUploading] = useState(false);
  const [addingGenre, setAddingGenre] = useState(false);
  const [newGenre, setNewGenre] = useState('');
  const [creatingGenre, setCreatingGenre] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [meR, taxR, trR] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/ai-library/taxonomy`),
        axios.get(`${API}/producer/tracks`),
      ]);
      if (!['producer', 'superadmin'].includes(meR.data?.role)) {
        toast.error('Producer access required');
        nav('/login', { replace: true });
        return;
      }
      setMe(meR.data);
      setTaxonomy(taxR.data);
      setTracks(trR.data || []);
      // Seed batch tags with sensible defaults so the first upload doesn't
      // require any tag interaction — producer can override anytime.
      if (!batchEnergy) setBatchEnergy(taxR.data.energies?.[0] || 'chill');
      if (!batchGenres.length && taxR.data.genres?.length) setBatchGenres([taxR.data.genres[0]]);
    } catch (err) {
      if (err?.response?.status === 401) {
        nav('/login', { replace: true });
        return;
      }
      toast.error('Failed to load producer dashboard');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const rows = files.map((file) => ({
      key: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      title: file.name.replace(/\.(mp3|m4a|wav)$/i, ''),
      artist: me?.name || '',
      status: 'staged',
      error: '',
      duration_ms: 0,
    }));
    // Read duration from the file so we don't have to compute it server-side
    rows.forEach((row) => {
      try {
        const el = document.createElement('audio');
        el.preload = 'metadata';
        el.src = URL.createObjectURL(row.file);
        el.addEventListener('loadedmetadata', () => {
          setStaged((prev) => prev.map((r) => (r.key === row.key ? { ...r, duration_ms: Math.floor((el.duration || 0) * 1000) } : r)));
          URL.revokeObjectURL(el.src);
        });
      } catch { /* noop */ }
    });
    setStaged((prev) => [...prev, ...rows]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const updateRow = (key, patch) => setStaged((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setStaged((prev) => prev.filter((r) => r.key !== key));

  const toggleBatchGenre = (g) => {
    // Each track carries exactly one genre — selecting one replaces the pick.
    setBatchGenres([g]);
  };

  const createGenre = async () => {
    const label = newGenre.trim();
    if (!label) return;
    setCreatingGenre(true);
    try {
      const r = await axios.post(`${API}/producer/genres`, { label });
      setTaxonomy((t) => ({ ...t, genres: r.data.genres }));
      setBatchGenres([r.data.slug]);
      setNewGenre('');
      setAddingGenre(false);
      toast.success(`Category "${label}" added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add category');
    } finally {
      setCreatingGenre(false);
    }
  };

  const uploadAll = async () => {
    if (!staged.length) return;
    if (!batchEnergy) return toast.error('Pick an energy for this batch');
    if (!batchGenres.length) return toast.error('Pick a genre for this batch');
    for (const r of staged) {
      if (!r.title.trim()) return toast.error(`"${r.file.name}" needs a title`);
    }
    setUploading(true);
    const upload = async (row) => {
      updateRow(row.key, { status: 'uploading', error: '' });
      try {
        const fd = new FormData();
        fd.append('file', row.file);
        fd.append('title', row.title);
        fd.append('artist', row.artist);
        fd.append('energy', batchEnergy);
        fd.append('genres', batchGenres.join(','));
        fd.append('duration_ms', String(row.duration_ms || 0));
        const r = await axios.post(`${API}/producer/tracks`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        updateRow(row.key, { status: 'done' });
        return r.data;
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Upload failed';
        updateRow(row.key, { status: 'error', error: detail });
        return null;
      }
    };
    // Two-at-a-time so we don't saturate a mobile uplink
    const queue = [...staged];
    const results = [];
    while (queue.length) {
      const batch = queue.splice(0, 2);
      const done = await Promise.all(batch.map(upload));
      results.push(...done);
    }
    const succeeded = results.filter(Boolean);
    if (succeeded.length) {
      setTracks((prev) => [...succeeded, ...prev]);
      toast.success(`${succeeded.length} track${succeeded.length === 1 ? '' : 's'} uploaded`);
    }
    setStaged((prev) => prev.filter((r) => r.status !== 'done'));
    setUploading(false);
  };

  const togglePreview = (t) => {
    if (playingId === t.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (!audioRef.current) audioRef.current = new Audio();
    audioRef.current.src = resolveSrc(t.url);
    audioRef.current.play();
    audioRef.current.onended = () => setPlayingId(null);
    setPlayingId(t.id);
  };

  const handleLogout = async () => {
    // Clear BOTH the Google session and the legacy JWT, and reset the
    // in-memory auth state — otherwise checkAuth() re-authenticates on /login
    // and bounces the producer straight back to this dashboard.
    await logout();
    nav('/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF007F] font-bold">Producer Studio</p>
          <h1 className="text-2xl font-extrabold tracking-tight">{me?.name || me?.email}</h1>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="producer-logout"><LogOut className="w-4 h-4 mr-2" /> Log out</Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Batch upload */}
        <section className="bg-white/[0.03] border border-white/10 rounded-3xl p-6" data-testid="producer-upload-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold">Upload batch</h2>
              <p className="text-sm text-gray-400 mt-0.5">MP3 / M4A / WAV · max 20 MB each · tag once, upload many.</p>
            </div>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="audio/mpeg,audio/mp4,audio/wav,.mp3,.m4a,.wav" multiple onChange={handleFilesSelected} className="hidden" data-testid="producer-file-input" />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="producer-add-files-btn"><Upload className="w-4 h-4 mr-2" /> Add files</Button>
              <Button onClick={uploadAll} disabled={!staged.length || uploading} data-testid="producer-upload-all-btn" className="bg-[#FF007F] hover:bg-[#CC0066]">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</> : <><Save className="w-4 h-4 mr-2" /> Upload {staged.length} track{staged.length === 1 ? '' : 's'}</>}
              </Button>
            </div>
          </div>

          {/* Batch tags — apply to every file in this drop */}
          <div className="bg-black/40 border border-white/10 rounded-2xl p-4 mb-4" data-testid="producer-batch-tags">
            <p className="text-xs text-gray-400 mb-3">These tags apply to <span className="text-white font-semibold">every file</span> in this batch.</p>
            <div className="mb-3">
              <Label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Energy</Label>
              <div className="flex flex-wrap gap-1.5">
                {taxonomy.energies.map((e) => (
                  <button key={e} type="button" disabled={uploading} onClick={() => setBatchEnergy(e)}
                    data-testid={`producer-batch-energy-${e}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${batchEnergy === e ? 'bg-[#FF007F] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 uppercase tracking-wider block mb-1.5">Genre</Label>
              <div className="flex flex-wrap gap-1.5 items-center">
                {taxonomy.genres.map((g) => (
                  <button key={g} type="button" disabled={uploading} onClick={() => toggleBatchGenre(g)}
                    data-testid={`producer-batch-genre-${g}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${batchGenres.includes(g) ? 'bg-[#39FF14] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
                    {g.replace(/-/g, ' ')} <span className="opacity-60">({taxonomy.genre_counts?.[g] ?? 0})</span>
                  </button>
                ))}
                {addingGenre ? (
                  <span className="inline-flex items-center gap-1">
                    <input
                      autoFocus
                      value={newGenre}
                      onChange={(e) => setNewGenre(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') createGenre(); if (e.key === 'Escape') { setAddingGenre(false); setNewGenre(''); } }}
                      placeholder="New category…"
                      disabled={creatingGenre}
                      data-testid="producer-new-genre-input"
                      className="px-3 py-1 rounded-full text-xs bg-black/50 border border-[#39FF14]/40 text-white placeholder-gray-500 focus:outline-none focus:border-[#39FF14] w-36"
                    />
                    <button type="button" onClick={createGenre} disabled={creatingGenre || !newGenre.trim()}
                      data-testid="producer-new-genre-save"
                      className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#39FF14] text-black disabled:opacity-40">
                      {creatingGenre ? '…' : 'Add'}
                    </button>
                    <button type="button" onClick={() => { setAddingGenre(false); setNewGenre(''); }}
                      data-testid="producer-new-genre-cancel"
                      className="px-2 py-1 rounded-full text-xs text-gray-400 hover:text-white">✕</button>
                  </span>
                ) : (
                  <button type="button" disabled={uploading} onClick={() => setAddingGenre(true)}
                    data-testid="producer-add-genre-btn"
                    className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-all">
                    + New category
                  </button>
                )}
              </div>
            </div>
          </div>

          {!staged.length ? (
            <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center bg-black/20">
              <Music className="w-8 h-8 mx-auto text-white/20 mb-2" />
              <p className="text-sm text-gray-400">Drop audio files here or click "Add files". They'll all inherit the batch tags above.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="producer-staged-list">
              {staged.map((r) => (
                <StagedRow key={r.key} row={r} onChange={(patch) => updateRow(r.key, patch)} onRemove={() => removeRow(r.key)} disabled={uploading} />
              ))}
            </div>
          )}
        </section>

        {/* Existing library */}
        <section data-testid="producer-library-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-lg font-bold">Your library <span className="text-gray-500 text-sm font-normal">({tracks.length})</span></h2>
          </div>
          {tracks.length === 0 ? (
            <p className="text-sm text-gray-500">No tracks yet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2" data-testid="producer-tracks-grid">
              {tracks.map((t) => (
                <TrackRow key={t.id} t={t} playing={playingId === t.id} onToggle={() => togglePreview(t)} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StagedRow({ row, onChange, onRemove, disabled }) {
  return (
    <div className={`bg-black/40 border rounded-xl px-3 py-2 flex items-center gap-3 ${row.status === 'error' ? 'border-red-500/40' : 'border-white/10'}`}>
      <div className="min-w-0 flex-1 grid grid-cols-2 gap-2">
        <Input value={row.title} onChange={(e) => onChange({ title: e.target.value })} disabled={disabled} placeholder="Title" className="bg-black/50 border-white/10 h-9 text-sm" />
        <Input value={row.artist} onChange={(e) => onChange({ artist: e.target.value })} disabled={disabled} placeholder="Artist" className="bg-black/50 border-white/10 h-9 text-sm" />
      </div>
      <div className="text-[11px] text-gray-500 min-w-[70px] text-right">{(row.file.size / (1024 * 1024)).toFixed(1)} MB</div>
      <div className="min-w-[70px] flex justify-end">
        {row.status === 'uploading' && <Loader2 className="w-4 h-4 text-white/40 animate-spin" />}
        {row.status === 'done' && <span className="text-xs text-[#39FF14]">Done</span>}
        {row.status === 'error' && <span className="text-xs text-red-400 truncate" title={row.error}>Failed</span>}
      </div>
      <Button size="sm" variant="ghost" onClick={onRemove} disabled={disabled}><Trash2 className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

function TrackRow({ t, playing, onToggle }) {
  return (
    <div className="flex items-center justify-between bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3" data-testid={`producer-track-${t.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <button type="button" onClick={onToggle} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center flex-shrink-0" data-testid={`producer-track-play-${t.id}`}>
          {playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
        </button>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{t.title}</p>
          <p className="text-xs text-gray-500 truncate">{t.artist || '—'} · {formatDuration(t.duration_ms)}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-[10px] uppercase tracking-wider bg-[#FF007F]/20 text-[#FF007F] rounded-full px-2 py-0.5">{t.energy}</span>
        {t.genres.slice(0, 3).map((g) => (
          <span key={g} className="text-[10px] bg-white/5 text-gray-400 rounded-full px-2 py-0.5">{g.replace(/-/g, ' ')}</span>
        ))}
      </div>
    </div>
  );
}
